import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { Role } from '../../models';

@Component({
  selector: 'app-role-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDividerModule
  ],
  template: `
    <h2 mat-dialog-title>{{ data.role ? 'Editar Rol' : 'Nuevo Rol' }}</h2>

    <mat-dialog-content>
      <form [formGroup]="form" class="role-form">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Nombre del rol</mat-label>
          <input matInput formControlName="name" placeholder="Ej: Supervisor">
          <mat-error *ngIf="form.get('name')?.hasError('required')">
            El nombre es requerido
          </mat-error>
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Descripción</mat-label>
          <textarea matInput formControlName="description" rows="2"
                    placeholder="Describe las responsabilidades de este rol"></textarea>
        </mat-form-field>

        <mat-divider></mat-divider>

        <div class="permissions-section">
          <h3>Permisos</h3>

          <div class="permission-groups">
            <div class="permission-group" *ngFor="let group of permissionGroups">
              <h4>{{ group.name }}</h4>
              <div class="permission-checkboxes">
                <mat-checkbox
                  *ngFor="let perm of group.permissions"
                  [checked]="isPermissionSelected(perm)"
                  (change)="togglePermission(perm)">
                  {{ formatPermission(perm) }}
                </mat-checkbox>
              </div>
            </div>
          </div>
        </div>
      </form>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()">Cancelar</button>
      <button mat-raised-button color="primary" (click)="onSave()" [disabled]="form.invalid">
        {{ data.role ? 'Guardar Cambios' : 'Crear Rol' }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    :host {
      display: block;
    }

    h2[mat-dialog-title] {
      font-family: var(--font-display);
      font-weight: 700;
      letter-spacing: -0.02em;
    }

    .role-form {
      display: flex;
      flex-direction: column;
      gap: 8px;
      min-width: 500px;
      padding-top: 8px;
    }

    .full-width {
      width: 100%;
    }

    mat-form-field {
      ::ng-deep .mdc-text-field--outlined {
        border-radius: 10px;
      }
    }

    mat-divider {
      margin: 16px 0;
    }

    .permissions-section {
      h3 {
        font-family: var(--font-display);
        font-size: 14px;
        font-weight: 700;
        color: var(--text-primary);
        margin: 0 0 16px 0;
      }
    }

    .permission-groups {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 24px;
    }

    .permission-group {
      h4 {
        font-size: 12px;
        font-weight: 600;
        color: var(--text-secondary);
        text-transform: uppercase;
        margin: 0 0 8px 0;
      }
    }

    .permission-checkboxes {
      display: flex;
      flex-direction: column;
      gap: 4px;

      mat-checkbox {
        font-size: 13px;
      }
    }

    mat-dialog-content {
      max-height: 70vh;
    }

    mat-dialog-actions {
      padding: 16px 0 0 0;
    }
  `]
})
export class RoleDialogComponent {
  form: FormGroup;
  selectedPermissions: Set<string>;
  permissionGroups: { name: string; permissions: string[] }[] = [];

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<RoleDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { role?: Role; permissions: string[] }
  ) {
    this.selectedPermissions = new Set(data.role?.permissions || []);

    this.form = this.fb.group({
      name: [data.role?.name || '', Validators.required],
      description: [data.role?.description || '']
    });

    this.groupPermissions();
  }

  groupPermissions() {
    const groups: Record<string, string[]> = {};

    this.data.permissions.forEach(perm => {
      const [module] = perm.split('.');
      const moduleName = this.getModuleName(module);

      if (!groups[moduleName]) {
        groups[moduleName] = [];
      }
      groups[moduleName].push(perm);
    });

    this.permissionGroups = Object.entries(groups).map(([name, permissions]) => ({
      name,
      permissions
    }));
  }

  getModuleName(module: string): string {
    const names: Record<string, string> = {
      'users': 'Usuarios',
      'roles': 'Roles',
      'pages': 'Páginas',
      'audit': 'Auditorías',
      'reports': 'Reportes',
      'settings': 'Configuración'
    };
    return names[module] || module;
  }

  formatPermission(permission: string): string {
    const [, action] = permission.split('.');
    const actions: Record<string, string> = {
      'read': 'Ver',
      'write': 'Crear/Editar',
      'delete': 'Eliminar',
      'execute': 'Ejecutar'
    };
    return actions[action] || action;
  }

  isPermissionSelected(permission: string): boolean {
    return this.selectedPermissions.has(permission);
  }

  togglePermission(permission: string) {
    if (this.selectedPermissions.has(permission)) {
      this.selectedPermissions.delete(permission);
    } else {
      this.selectedPermissions.add(permission);
    }
  }

  onCancel() {
    this.dialogRef.close();
  }

  onSave() {
    if (this.form.valid) {
      this.dialogRef.close({
        ...this.form.value,
        permissions: Array.from(this.selectedPermissions)
      });
    }
  }
}
